import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { 
  Upload, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Image, 
  FileWarning,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const BulkRegistrationCardUpload = ({ 
  onSuccess,
  getAuthHeader,
  API,
  externalOpen = null,
  onExternalOpenChange = null
}) => {
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const dialogOpen = externalOpen !== null ? externalOpen : internalDialogOpen;
  const setDialogOpen = onExternalOpenChange || setInternalDialogOpen;
  
  const [step, setStep] = useState('check'); // 'check', 'upload', 'preview', 'uploading', 'complete'
  const [loading, setLoading] = useState(false);
  const [missingData, setMissingData] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [matchResults, setMatchResults] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [batchId, setBatchId] = useState(null);

  // Check for missing registration cards when dialog opens
  useEffect(() => {
    if (dialogOpen && step === 'check') {
      checkMissingCards();
    }
  }, [dialogOpen]);

  const checkMissingCards = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/motorcycles/missing-registration-cards`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      
      if (data.success) {
        setMissingData(data);
        if (data.total_affected === 0) {
          toast.success('All motorcycles have valid registration cards!');
        }
      } else {
        toast.error('Failed to check missing cards');
      }
    } catch (error) {
      toast.error('Error checking missing cards');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleZipChange = (e) => {
    setZipFile(e.target.files[0]);
    setMatchResults(null);
  };

  const handleUploadZip = async () => {
    if (!zipFile) {
      toast.error('Please select a ZIP file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('images_zip', zipFile);

      const response = await fetch(`${API}/motorcycles/bulk-upload-registration-cards`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        setMatchResults(data);
        setBatchId(data.batch_id);
        setStep('preview');
        
        if (data.matched_count > 0) {
          toast.success(`Matched ${data.matched_count} images to motorcycles`);
        }
        if (data.unmatched_count > 0) {
          toast.warning(`${data.unmatched_count} images could not be matched`);
        }
      } else {
        toast.error(data.detail || 'Failed to process ZIP file');
      }
    } catch (error) {
      toast.error('Error processing ZIP file');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!matchResults || !matchResults.matched || matchResults.matched.length === 0) {
      toast.error('No matched images to upload');
      return;
    }

    setStep('uploading');
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/motorcycles/confirm-registration-cards-upload`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          batch_id: batchId,
          matched: matchResults.matched
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setUploadResults(data);
        setStep('complete');
        
        if (data.uploaded_count > 0) {
          toast.success(`Uploaded ${data.uploaded_count} registration cards to SharePoint`);
        }
        if (data.failed_count > 0) {
          toast.warning(`${data.failed_count} uploads failed`);
        }
        if (data.onedrive_error) {
          toast.error(`OneDrive Error: ${data.onedrive_error}`);
        }
      } else {
        toast.error(data.detail || 'Upload failed');
        setStep('preview');
      }
    } catch (error) {
      toast.error('Error uploading registration cards');
      console.error(error);
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setStep('check');
    setMissingData(null);
    setZipFile(null);
    setMatchResults(null);
    setUploadResults(null);
    setBatchId(null);
  };

  const handleClose = () => {
    setDialogOpen(false);
    if (step === 'complete' && uploadResults?.uploaded_count > 0) {
      onSuccess?.();
    }
    resetDialog();
  };

  return (
    <>
      {/* Show button only when not externally controlled */}
      {externalOpen === null && (
        <Button
          variant="outline"
          onClick={() => setDialogOpen(true)}
          className="text-xs px-2 py-1.5 h-8 whitespace-nowrap border-orange-300 text-orange-700 hover:bg-orange-50"
          data-testid="fix-registration-cards-btn"
        >
          <FileWarning className="w-3 h-3 mr-1" />
          Fix Missing Cards
        </Button>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) handleClose(); else setDialogOpen(true); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fix Missing Registration Cards</DialogTitle>
            <DialogDescription>
              Upload registration card images to fix motorcycles with missing or broken card URLs.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Step 1: Check Missing Cards */}
            {step === 'check' && (
              <div className="space-y-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    <span className="ml-2">Checking for missing registration cards...</span>
                  </div>
                ) : missingData ? (
                  <>
                    {missingData.total_affected === 0 ? (
                      <Alert className="border-green-500 bg-green-50">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <AlertDescription>
                          <p className="font-semibold text-green-900">All Good!</p>
                          <p className="text-sm text-green-700">All motorcycles have valid registration card URLs.</p>
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        <Alert className="border-orange-500 bg-orange-50">
                          <AlertTriangle className="w-4 h-4 text-orange-600" />
                          <AlertDescription>
                            <p className="font-semibold text-orange-900">
                              {missingData.total_affected} Motorcycles Need Registration Cards
                            </p>
                            <div className="text-sm text-orange-700 mt-1 space-y-1">
                              {missingData.total_missing > 0 && (
                                <p>• <strong>{missingData.total_missing}</strong> motorcycles have no registration card</p>
                              )}
                              {missingData.total_broken > 0 && (
                                <p>• <strong>{missingData.total_broken}</strong> motorcycles have broken URLs (files lost after deployment)</p>
                              )}
                            </div>
                          </AlertDescription>
                        </Alert>

                        {/* Show affected motorcycles */}
                        <div className="max-h-60 overflow-auto border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-xs">Plate Number</TableHead>
                                <TableHead className="text-xs">Chassis Number</TableHead>
                                <TableHead className="text-xs">Status</TableHead>
                                <TableHead className="text-xs">Issue</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {[...missingData.broken, ...missingData.missing].slice(0, 20).map((m, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="text-xs font-mono">{m.plate_number}</TableCell>
                                  <TableCell className="text-xs font-mono">{m.chassis_number}</TableCell>
                                  <TableCell className="text-xs">
                                    <Badge variant="outline">{m.status}</Badge>
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {m.registration_card_url ? (
                                      <Badge className="bg-red-100 text-red-800">Broken URL</Badge>
                                    ) : (
                                      <Badge className="bg-orange-100 text-orange-800">Missing</Badge>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          {missingData.total_affected > 20 && (
                            <p className="text-xs text-center py-2 text-slate-500">
                              ... and {missingData.total_affected - 20} more
                            </p>
                          )}
                        </div>

                        <Button
                          onClick={() => setStep('upload')}
                          className="w-full bg-gradient-to-r from-orange-500 to-amber-500"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Registration Cards
                        </Button>
                      </>
                    )}
                  </>
                ) : null}
              </div>
            )}

            {/* Step 2: Upload ZIP */}
            {step === 'upload' && (
              <div className="space-y-4">
                <Alert className="border-blue-500 bg-blue-50">
                  <Image className="w-4 h-4 text-blue-600" />
                  <AlertDescription>
                    <p className="font-semibold text-blue-900">Upload Instructions</p>
                    <ul className="text-sm text-blue-700 mt-1 list-disc list-inside space-y-1">
                      <li>Create a ZIP file containing registration card images (JPG/PNG)</li>
                      <li>Name each image by <strong>Plate Number</strong> or <strong>Chassis Number</strong></li>
                      <li>Example: <code className="bg-blue-100 px-1 rounded">DXB 2 33654.jpg</code> or <code className="bg-blue-100 px-1 rounded">ME4JF642NPT100011.jpg</code></li>
                    </ul>
                  </AlertDescription>
                </Alert>

                <div className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Image className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Registration Cards ZIP File</span>
                  </div>
                  <input
                    type="file"
                    accept=".zip"
                    onChange={handleZipChange}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    data-testid="registration-cards-zip-input"
                  />
                  {zipFile && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 mt-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>{zipFile.name}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep('check')}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleUploadZip}
                    disabled={!zipFile || loading}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500"
                    data-testid="process-zip-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Match Images to Motorcycles
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Preview Matches */}
            {step === 'preview' && matchResults && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-2xl font-bold text-green-700">{matchResults.matched_count}</div>
                    <div className="text-sm text-green-600">Images Matched</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="text-2xl font-bold text-orange-700">{matchResults.unmatched_count}</div>
                    <div className="text-sm text-orange-600">Images Unmatched</div>
                  </div>
                </div>

                {/* Matched List */}
                {matchResults.matched.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2 text-green-800">
                      ✓ Ready to Upload ({matchResults.matched.length})
                    </h4>
                    <div className="max-h-40 overflow-auto border rounded-lg bg-green-50">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Image File</TableHead>
                            <TableHead className="text-xs">Plate Number</TableHead>
                            <TableHead className="text-xs">Chassis Number</TableHead>
                            <TableHead className="text-xs">Match Type</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {matchResults.matched.map((m, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-xs font-mono">{m.original_filename}</TableCell>
                              <TableCell className="text-xs">{m.plate_number}</TableCell>
                              <TableCell className="text-xs font-mono">{m.chassis_number}</TableCell>
                              <TableCell className="text-xs">
                                <Badge className="bg-green-100 text-green-800">{m.match_type}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Unmatched List */}
                {matchResults.unmatched.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2 text-orange-800">
                      ✗ Could Not Match ({matchResults.unmatched_count})
                    </h4>
                    <div className="max-h-32 overflow-auto p-2 border rounded-lg bg-orange-50 text-xs font-mono">
                      {matchResults.unmatched.map((name, idx) => (
                        <span key={idx} className="inline-block mr-2 mb-1 px-2 py-1 bg-orange-100 rounded">
                          {name}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-orange-600 mt-1">
                      These images don't match any plate number or chassis number in the system.
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep('upload')}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleConfirmUpload}
                    disabled={matchResults.matched.length === 0 || loading}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500"
                    data-testid="confirm-registration-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload {matchResults.matched.length} Cards to SharePoint
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Uploading */}
            {step === 'uploading' && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
                <p className="text-lg font-medium">Uploading to SharePoint...</p>
                <p className="text-sm text-slate-500">This may take a few minutes</p>
              </div>
            )}

            {/* Step 5: Complete */}
            {step === 'complete' && uploadResults && (
              <div className="space-y-4">
                <Alert className={uploadResults.failed_count === 0 ? "border-green-500 bg-green-50" : "border-orange-500 bg-orange-50"}>
                  {uploadResults.failed_count === 0 ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                  )}
                  <AlertDescription>
                    <p className="font-semibold">
                      {uploadResults.failed_count === 0 ? 'Upload Complete!' : 'Upload Completed with Some Errors'}
                    </p>
                    <div className="text-sm mt-1">
                      <p>✓ <strong>{uploadResults.uploaded_count}</strong> registration cards uploaded successfully</p>
                      {uploadResults.failed_count > 0 && (
                        <p className="text-red-700">✗ <strong>{uploadResults.failed_count}</strong> uploads failed</p>
                      )}
                      {uploadResults.onedrive_error && (
                        <p className="text-red-700 mt-2">
                          <strong>OneDrive Error:</strong> {uploadResults.onedrive_error}
                        </p>
                      )}
                      {!uploadResults.onedrive_available && (
                        <p className="text-orange-700 mt-2">
                          ⚠️ SharePoint/OneDrive was not available. Check Azure credentials in .env file.
                        </p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>

                {/* Results Table */}
                <div className="max-h-60 overflow-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Plate Number</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uploadResults.results.map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-xs">{r.plate_number}</TableCell>
                          <TableCell className="text-xs">
                            {r.status === 'success' ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Success
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800">
                                <XCircle className="w-3 h-3 mr-1" />
                                Failed
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <Button
                  onClick={handleClose}
                  className="w-full"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkRegistrationCardUpload;
